import React, { useState } from "react";
import { useI18n } from "../../i18n";
import { categoryIcon } from "../utils/categories";
import { groupStats } from "../utils/groups";
import { productImageUrl } from "../utils/images";
import { formatAmount } from "../utils/money";

/**
 * A range collapsed to one tile: "Vozol Gear 12000", with what is left across
 * all its flavours. One tap opens it; the tile itself never adds anything.
 */
const GroupTile = ({ group, members, quantity, lowStockThreshold, onOpen }) => {
  const { t } = useI18n();
  const [imageFailed, setImageFailed] = useState(false);
  const stats = groupStats(members, lowStockThreshold);

  const image = group.image || members.find((member) => member.image)?.image;
  const imageSrc = imageFailed ? null : productImageUrl(image);

  return (
    <button
      type="button"
      onClick={() => onOpen(group)}
      aria-label={t("pos.group.open", { name: group.name, count: stats.count })}
      className={`group relative flex flex-col text-start rounded-2xl overflow-hidden border transition-all active:scale-[0.97] ${
        quantity > 0
          ? "border-emerald-400 bg-emerald-500/10 shadow-lg shadow-emerald-500/10"
          : "border-slate-800 bg-slate-900 hover:border-slate-600 hover:bg-slate-800"
      } ${stats.allOut ? "opacity-60" : ""}`}
    >
      <div className="relative h-20 sm:h-24 bg-slate-800/60 flex items-center justify-center overflow-hidden">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            loading="lazy"
            onError={() => setImageFailed(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-3xl">{categoryIcon(members[0]?.category)}</span>
        )}

        <div className="absolute top-1.5 start-1.5 flex flex-col items-start gap-1">
          {stats.hasOffer && (
            <span className="px-1.5 py-0.5 rounded-md bg-amber-500 text-slate-900 text-[10px] font-bold">
              {t("pos.tile.offer")}
            </span>
          )}
          {stats.allOut ? (
            <span className="px-1.5 py-0.5 rounded-md bg-red-500/90 text-white text-[10px] font-bold uppercase tracking-wide">
              {t("pos.tile.out")}
            </span>
          ) : (
            stats.needAttention > 0 && (
              <span className="px-1.5 py-0.5 rounded-md bg-amber-500/90 text-slate-900 text-[10px] font-bold tabular-nums">
                {t("pos.group.attention", { count: stats.needAttention })}
              </span>
            )
          )}
        </div>

        <span className="absolute top-1.5 end-1.5 px-1.5 py-0.5 rounded-md bg-slate-950/70 text-slate-200 text-[10px] font-bold tabular-nums">
          {t("pos.group.variants", { count: stats.count })}
        </span>

        {quantity > 0 && (
          <span className="absolute bottom-1.5 end-1.5 min-w-6 h-6 px-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center justify-center tabular-nums">
            {quantity}
          </span>
        )}
      </div>

      <div className="flex-1 flex flex-col p-2.5">
        <p className="text-[13px] leading-tight font-semibold text-white line-clamp-2">
          {group.name}
        </p>
        {group.brand && (
          <p className="text-[11px] text-slate-400 truncate mt-0.5">
            {group.brand}
          </p>
        )}
        <div className="flex flex-wrap items-baseline gap-x-1.5 mt-auto pt-1.5">
          <span className="text-base font-bold text-emerald-400 tabular-nums">
            {stats.pricesDiffer && (
              <span className="text-[11px] font-semibold text-slate-400 me-1">
                {t("pos.group.from")}
              </span>
            )}
            {formatAmount(stats.lowestPrice)}
          </span>
          <span className="text-[11px] text-slate-400 tabular-nums">
            {t("pos.group.inStock", { count: stats.total })}
          </span>
        </div>
      </div>
    </button>
  );
};

export default GroupTile;
